import { Component } from '@theme/component';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number | undefined} */
  #requestTimeout;
  #disabledControls = new Map();

  /** @type {{ input: HTMLInputElement, descriptionId?: string, invalid?: string | null } | null} */
  #errorAssociation = null;

  #clearErrorAssociation() {
    const state = this.#errorAssociation;
    if (!state) return;
    if (state.descriptionId) {
      const ids = (state.input.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean)
        .filter((id) => id !== state.descriptionId);
      if (ids.length) state.input.setAttribute('aria-describedby', ids.join(' '));
      else state.input.removeAttribute('aria-describedby');
    }
    if ('invalid' in state && state.input.getAttribute('aria-invalid') === 'true') {
      if (state.invalid === null) state.input.removeAttribute('aria-invalid');
      else if (state.invalid !== undefined) state.input.setAttribute('aria-invalid', state.invalid);
    }
    this.#errorAssociation = null;
  }

  /** @param {boolean} [invalid] */
  #associateError(invalid = false) {
    this.#clearErrorAssociation();
    const input = this.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;
    /** @type {{ input: HTMLInputElement, descriptionId?: string, invalid?: string | null }} */
    const state = { input };
    const errorId = this.refs.cartDiscountError.id;
    const descriptions = (input.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean);
    if (errorId && !descriptions.includes(errorId)) {
      input.setAttribute('aria-describedby', [...descriptions, errorId].join(' '));
      state.descriptionId = errorId;
    }
    if (invalid) {
      state.invalid = input.getAttribute('aria-invalid');
      input.setAttribute('aria-invalid', 'true');
    }
    this.#errorAssociation = state;
  }

  clearError = () => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.querySelector('[data-cart-request-error]')?.remove();
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    this.#clearErrorAssociation();
  };

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    clearTimeout(this.#requestTimeout);
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.#requestTimeout = setTimeout(() => {
      if (this.#activeFetch !== abortController) return;
      this.#showRequestError();
      abortController.abort();
      this.#finishRequest(abortController);
    }, 15000);
    this.setAttribute('aria-busy', 'true');
    for (const control of this.querySelectorAll('button, input')) {
      this.#disabledControls.set(control, control.disabled);
      control.disabled = true;
    }
    return abortController;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const controller = this.#activeFetch;
    controller?.abort();
    if (controller) this.#finishRequest(controller);
    this.clearError();
  }

  /** @param {AbortController} controller */
  #ownsRequest(controller) {
    return this.#activeFetch === controller && !controller.signal.aborted;
  }

  /** @param {AbortController} controller */
  #finishRequest(controller) {
    if (this.#activeFetch !== controller) return;
    clearTimeout(this.#requestTimeout);
    this.#requestTimeout = undefined;
    this.#activeFetch = null;
    this.removeAttribute('aria-busy');
    for (const [control, disabled] of this.#disabledControls) control.disabled = disabled;
    this.#disabledControls.clear();
  }

  /** @param {string} [message] */
  #showRequestError(message = Theme.translations.cart_update_error) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    let error = cartDiscountError.querySelector('[data-cart-request-error]');
    if (!error) {
      error = document.createElement('small');
      error.className = 'cart-discount__error-text cart-primary-typography';
      error.setAttribute('data-cart-request-error', '');
      cartDiscountError.append(error);
    }
    error.textContent = message;
    cartDiscountError.classList.remove('hidden');
    this.#associateError();
  }

  /** @param {HTMLInputElement} input */
  #restoreRejectedCodeFocus(input) {
    if (!this.isConnected || !input.isConnected || input.disabled || !input.getClientRects().length) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    const dialog = input.closest('dialog');
    if (dialog && !dialog.open) return;
    if (input.closest('[hidden], [inert], [aria-hidden="true"]')) return;
    if (getComputedStyle(input).visibility === 'hidden') return;
    if (Array.from(document.querySelectorAll('dialog[open]')).some((openDialog) => !openDialog.contains(input))) return;
    input.focus({ preventScroll: true });
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#activeFetch) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;
    const formHadFocus = form.contains(document.activeElement);
    let rejectedCode = false;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      this.clearError();
      if (existingDiscounts.includes(discountCodeValue)) return;

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      if (!this.#ownsRequest(abortController)) return;
      if (!response.ok) {
        this.#showRequestError(typeof data.description === 'string' ? data.description :
          typeof data.message === 'string' ? data.message : undefined);
        return;
      }

      if (!Array.isArray(data.discount_codes)) throw new Error('Discount response is missing codes');
      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        rejectedCode = true;
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || !newHtml.trim()) throw new Error('Discount response is missing markup');
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (!section) throw new Error('Discount response has no matching section');
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (this.#ownsRequest(abortController)) this.#showRequestError();
    } finally {
      const ownedRequest = this.#ownsRequest(abortController);
      this.#finishRequest(abortController);
      if (ownedRequest && rejectedCode && formHadFocus) this.#restoreRejectedCodeFocus(discountCode);
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#activeFetch) return;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      this.clearError();
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      if (!this.#ownsRequest(abortController)) return;
      if (!response.ok) {
        this.#showRequestError(typeof data.description === 'string' ? data.description :
          typeof data.message === 'string' ? data.message : undefined);
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || !newHtml.trim()) throw new Error('Discount response is missing markup');
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      if (!parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`)) {
        throw new Error('Discount response has no matching section');
      }
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (this.#ownsRequest(abortController)) this.#showRequestError();
    } finally {
      this.#finishRequest(abortController);
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    this.#associateError(type === 'discount_code');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

